/**
 * ConversationPane.tsx — centre column: active conversation (Design Spec §03/§04/§05).
 *
 * Driven by the selected inbox row (no extra detail round-trip needed — the row
 * carries accountId / fanMemberId / maloumChatId / isMuted). Renders:
 *   - fan header (avatar, display name, mono id, always-on DE⇄EN indicator, health)
 *   - bilingual message thread (English primary, German secondary — ARCH §8.1)
 *   - send composer (EN→DE confirmed preview + PPV — D-12)
 *
 * Dead/needs-reauth accounts disable the composer (D-06). Times Europe/Berlin (D-09).
 */

import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  authApi,
  chattersApi,
  conversationsApi,
  fansApi,
  type MessageDto,
  type PendingSendDto,
} from '../lib/api-client.js';
import type { InboxConversationDto, HealthUpdate } from '@crm/types';
import { useToast } from '@crm/ui';
import { useWsClient } from '../lib/WsProvider.js';
import { MessageThread } from './MessageThread.js';
import { SendComposer, type RetrySeed } from './SendComposer.js';
import { AccountHealthBadge } from './AccountHealthBadge.js';
import { FanAvatar } from './FanAvatar.js';
import { CollisionChip } from './CollisionChip.js';
import { fanDisplayName } from '../lib/format.js';
import { mergeMessagesById, mergeHeadPage, headWindowDisjoint } from '../lib/message-merge.js';

interface ConversationPaneProps {
  conv: InboxConversationDto;
  /** Mark this conversation read (owned by ChatterWorkspace — optimistic cache patch). */
  onMarkRead?: (conv: InboxConversationDto) => void;
  onMessageSent?: () => void;
  /**
   * Hides the WRITE controls only — composer, mute toggle, delete/retry affordances (D-02).
   * Translation stays available (owner 2026-07-19) — it only refreshes the CRM's display text.
   * Omitted/false for the chatter surface AND for the admin full-parity surface (owner
   * 2026-07-20: admins send too). Passive-on-open side-effects are governed separately by
   * suppressPassiveClaim (below) + by onMarkRead being omitted.
   */
  readOnly?: boolean;
  /**
   * Suppress the passive collision claim — do NOT register this viewer on the conversation
   * (conversation.claimed_by_chatter_id) on open (T-12-11). Set for the admin oversight surface:
   * an admin scanning dozens of accounts must not stamp "being viewed by {admin}" on every
   * thread they glance at, even though they CAN send. Omitted/false for the chatter surface
   * (byte-identical — chatters always claim on open).
   */
  suppressPassiveClaim?: boolean;
}

/** How close to the bottom (px) still counts as "pinned to the newest message". */
const NEAR_BOTTOM_PX = 160;

export function ConversationPane({
  conv,
  onMarkRead,
  onMessageSent,
  readOnly = false,
  suppressPassiveClaim = false,
}: ConversationPaneProps) {
  const queryClient = useQueryClient();
  const ws = useWsClient();
  const { addToast } = useToast();
  const conversationId = conv.id;

  // Mute — CRM-local (no Maloum call). Moved from the fan panel to the thread
  // header (owner 2026-07-01). Invalidating ['inbox'] drops the muted row from
  // the active queue at once; ['fan', …] refreshes the panel's status badge.
  // setQueriesData patches isMuted into EVERY cached inbox list (all filter modes):
  // when the open row was dropped by a server-side filter chip, this conv prop comes
  // from a cached-list/ref fallback that a refetch of the filtered key can never
  // refresh (the row stays excluded) — without the patch the header toggle froze at
  // the pre-mute state and unmuting from the header was impossible (review finding).
  const fanMemberId = conv.fanMemberId ?? null;
  const patchMutedEverywhere = (isMuted: boolean) => {
    queryClient.setQueriesData<InboxConversationDto[]>({ queryKey: ['inbox'] }, (old) =>
      old?.map((c) => (c.id === conv.id ? { ...c, isMuted } : c)),
    );
  };
  const muteMut = useMutation({
    mutationFn: () =>
      fanMemberId ? fansApi.mute(fanMemberId, conv.accountId) : Promise.resolve(undefined as void),
    onSuccess: () => {
      addToast('Fan muted', 'success');
      patchMutedEverywhere(true);
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['fan', fanMemberId, conv.accountId] });
    },
    onError: () => addToast('Mute failed', 'danger'),
  });
  const unmuteMut = useMutation({
    mutationFn: () =>
      fanMemberId ? fansApi.unmute(fanMemberId, conv.accountId) : Promise.resolve(undefined as void),
    onSuccess: () => {
      addToast('Fan unmuted', 'success');
      patchMutedEverywhere(false);
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['fan', fanMemberId, conv.accountId] });
    },
    onError: () => addToast('Unmute failed', 'danger'),
  });
  const muteBusy = muteMut.isPending || unmuteMut.isPending;

  const [healthState, setHealthState] = React.useState<string | null>(null);

  // Passive collision claim (D-05-18) — who else is on this conversation.
  const [claim, setClaim] = React.useState<{
    claimedByChatterId: string | null;
    claimedAt: string | null;
  }>({ claimedByChatterId: null, claimedAt: null });

  // Messages (paginated; reset whenever the selected conversation changes). The cursor
  // drives ONLY the older-history query — the live tail comes from a separate cursor-less
  // head query (below), so paging back never stops new inbound messages from rendering
  // (chatter-reported 2026-07-13).
  const [messageCursor, setMessageCursor] = React.useState<string | undefined>(undefined);
  // Mirror the cursor into a ref so the head-merge effect can read "has the chatter paged
  // yet?" for its hasMore gate without re-running (and re-merging) on every cursor change.
  const messageCursorRef = React.useRef<string | undefined>(undefined);
  messageCursorRef.current = messageCursor;
  const [allMessages, setAllMessages] = React.useState<MessageDto[]>([]);
  // Mirror allMessages so the head-merge effect can read the pre-merge list (for the
  // gap-collapse hasMore/cursor decision) without adding allMessages to its deps.
  const allMessagesRef = React.useRef<MessageDto[]>([]);
  allMessagesRef.current = allMessages;
  const [hasMore, setHasMore] = React.useState(false);

  // ---- Thread scroll orchestration (owner UX 2026-07-13) ----
  // ConversationPane remounts per conversation (keyed by conv.id in ChatterWorkspace),
  // so these refs start fresh on every open — no cross-conversation reset needed.
  // Model = "stick to bottom": while the chatter is pinned at the newest message, any
  // content growth (a new message, lazy media/thumbnails loading in after the open-snap,
  // a bubble expanding) re-pins to the bottom via a ResizeObserver; scrolling up releases
  // the pin so reading history is never yanked. "Load older" prepends above the viewport
  // and re-anchors so the chatter stays on what they were reading.
  const threadScrollRef = React.useRef<HTMLDivElement>(null); // the overflow-y-auto viewport
  const stickToBottomRef = React.useRef(true); // pinned to the newest message? (open = pinned)
  const initialSnapDoneRef = React.useRef(false); // has the one-time pre-paint snap run?
  const isPrependingRef = React.useRef(false); // a "Load older" re-anchor is in flight
  const prevScrollHeightRef = React.useRef(0); // viewport scrollHeight captured pre-prepend
  // Set by the OLDER-page merge so the re-anchor (layout effect branch A) consumes the
  // prepend flag on the older commit SPECIFICALLY — not on an interleaved head/tail commit.
  // The live-tail split (head + older are independent queries now) means a head refetch can
  // land mid-prepend; without this signal it would steal the flag and strand the prepend.
  const olderCommittedRef = React.useRef(false);
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);
  // In-flight/failed outbound sends (queued/sending/sent-unreconciled/failed) —
  // rendered as pending bubbles so a send is never invisible between the confirm
  // toast and the reconciler materializing the real message row.
  const [pendingSends, setPendingSends] = React.useState<PendingSendDto[]>([]);
  // Failed-send retry: seeds the composer with the failed text (fresh preview +
  // clientSendId — never a replay of the dead outbound_send row).
  const [retrySeed, setRetrySeed] = React.useState<RetrySeed | null>(null);

  // "Delete this message" (owner 2026-07-03). Ids optimistically deleted this session — the
  // message/pending-send derive effect below filters these out of EVERY server re-sync, so a
  // 5s poll or the onSettled refetch can't resurrect a just-deleted bubble (the reconciler
  // never clears the server-side deleted_at either). Cleared on conversation change. A failed
  // delete removes the id + restores the snapshot so the row comes back (the onSettled refetch
  // alone can't — with older history loaded it refetches an older cursor page, not page 0).
  const deletedMessageIds = React.useRef<Set<string>>(new Set());
  const deletedSendIds = React.useRef<Set<string>>(new Set());
  const deleteMessageMut = useMutation({
    mutationFn: (messageId: string) => conversationsApi.deleteMessage(conversationId, messageId),
    onMutate: (messageId: string) => {
      deletedMessageIds.current.add(messageId);
      let snapshot: MessageDto[] = [];
      setAllMessages((prev) => {
        snapshot = prev;
        return prev.filter((m) => m.id !== messageId);
      });
      return { snapshot };
    },
    onError: (_err, messageId, ctx) => {
      deletedMessageIds.current.delete(messageId);
      if (ctx?.snapshot) setAllMessages(ctx.snapshot);
      addToast('Delete failed', 'danger');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['messages', conversationId] }),
  });
  const deleteSendMut = useMutation({
    mutationFn: (sendId: string) => conversationsApi.deleteSend(conversationId, sendId),
    onMutate: (sendId: string) => {
      deletedSendIds.current.add(sendId);
      let snapshot: PendingSendDto[] = [];
      setPendingSends((prev) => {
        snapshot = prev;
        return prev.filter((s) => s.sendId !== sendId);
      });
      return { snapshot };
    },
    onError: (_err, sendId, ctx) => {
      deletedSendIds.current.delete(sendId);
      if (ctx?.snapshot) setPendingSends(ctx.snapshot);
      addToast('Delete failed', 'danger');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['messages', conversationId] }),
  });

  React.useEffect(() => {
    // New conversation selected — reset paging + health + claim.
    setMessageCursor(undefined);
    setAllMessages([]);
    setHasMore(false);
    setPendingSends([]);
    setRetrySeed(null);
    setHealthState(null);
    setClaim({ claimedByChatterId: null, claimedAt: null });
    // "Delete this message": the optimistic-delete id sets are per-conversation — clear them
    // so they don't leak across conversations (and never grow unbounded over a session).
    deletedMessageIds.current.clear();
    deletedSendIds.current.clear();
  }, [conversationId]);

  // ---- Current viewer + chatter roster (for CollisionChip displayName — SC-6) ----
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => authApi.me() });
  const currentChatterId = meQuery.data?.id ?? null;

  // GET /chatters is any-authenticated (Phase 9 CR-02) — resolve claimer id → displayName.
  const chattersQuery = useQuery({
    queryKey: ['chatters'],
    queryFn: () => chattersApi.list(),
    staleTime: 5 * 60_000,
  });
  const chatterNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const c of chattersQuery.data ?? []) map.set(c.id, c.displayName);
    return map;
  }, [chattersQuery.data]);

  // ---- Passive collision indicator (D-05-18 / SC-6 / UI-SPEC Surface 8) ----
  // Read the existing claim (is another chatter already here?) BEFORE claiming it
  // ourselves, so the chip can surface a collaborator. Then register our own claim
  // (best-effort) so OTHER chatters get the same ambient heads-up. Purely passive:
  // no hard lock, never blocks opening or sending. Our own claim is NOT echoed back
  // to us (the server skips the claimer), so it won't overwrite the displayed name.
  React.useEffect(() => {
    // Always READ the existing claim (a passive GET) so the viewer can SEE who else is handling
    // this chat — an admin overseeing many accounts benefits from the same coordination info a
    // chatter gets. Only the WRITE (registering OUR OWN claim) is suppressed on the oversight
    // surface: an admin must not stamp "being viewed by {admin}" on every thread they glance at,
    // nor pollute conversation.claimed_by_chatter_id with a non-chatter actor (T-12-11). Governed
    // by suppressPassiveClaim, NOT readOnly — admins can now send yet stay silent on merely
    // opening a thread (owner 2026-07-20).
    let cancelled = false;
    conversationsApi
      .get(conversationId)
      .then((detail) => {
        if (cancelled) return undefined;
        setClaim({
          claimedByChatterId: detail.claimed_by_chatter_id ?? null,
          claimedAt: detail.claimed_at ?? null,
        });
        if (suppressPassiveClaim) return undefined; // read-only viewer: never register our claim
        return conversationsApi.claim(conversationId);
      })
      .catch(() => {
        /* non-fatal — the collision indicator is passive/ambient */
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, suppressPassiveClaim]);

  // Live updates: another chatter claims this conversation while we're viewing it. Purely a
  // listener (no write), so it runs regardless of suppressPassiveClaim — an admin oversight
  // viewer still sees the chip update live when a chatter picks up the chat.
  React.useEffect(() => {
    const unsub = ws.onConversationClaim((evt) => {
      if (evt.id !== conversationId) return;
      setClaim({
        claimedByChatterId: evt.claimed_by_chatter_id,
        claimedAt: evt.claimed_at,
      });
    });
    return unsub;
  }, [ws, conversationId]);

  // HEAD query — the cursor-less "live tail" page (always the newest 50). This is the
  // query that polls and that every ['messages', conversationId] invalidation refetches
  // (inbox:delta, send, retranslate, delete). It ALWAYS carries the newest rows, so the
  // tail keeps flowing no matter how far back the chatter has paged — the older cursor
  // lives on a separate query (below) and only extends history backward. Before this split
  // the single cursor-keyed query, once "Load older" set a cursor, returned only rows OLDER
  // than the cursor on every poll/delta — so a new fan reply re-badged the inbox row but
  // never rendered in the open thread until a send or conversation switch (chatter-reported
  // 2026-07-13).
  //
  // Poll while sends are in flight (Queued → Sending → Sent → real message / Failed
  // transitions appear without a manual refresh), and while a clicked translation is in
  // flight ('stale' = the retranslate endpoint queued it) so the result pops in.
  // Manual-only translation (owner 2026-07-03): 'pending' rows wait for a Translate click
  // and must NOT hold the poll open — under an any-untranslated-row condition, threads with
  // old untranslated history would poll forever.
  //
  // The 'stale' poll is TIGHT (2s, owner 2026-07-13): the retranslate job finishes async
  // (~1-3s) but there is no WS push for translation completion, so the result is only
  // discovered on a poll. 2s + the per-click kick (MessageThread retranslateMut) brings a
  // lone translation in within a couple seconds. Only runs while a 'stale' row is on screen.
  const headQuery = useQuery({
    queryKey: ['messages', conversationId, 'head'],
    queryFn: () => conversationsApi.messages(conversationId, undefined, 50),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.pendingSends?.length) return 5_000;
      return data?.messages.some((m) => m.translationStatus === 'stale') ? 2_000 : false;
    },
  });

  // OLDER query — a cursor-driven page of history, enabled only once the chatter clicks
  // "Load older messages". Only the newest older page is observed (its cursor is the query
  // key), so it also carries the 2s poll that reveals an on-demand retranslate of a message
  // in THAT page. Pending sends are conversation-scoped and polled by the head query.
  const olderQuery = useQuery({
    queryKey: ['messages', conversationId, 'older', messageCursor],
    queryFn: () => conversationsApi.messages(conversationId, messageCursor, 50),
    enabled: messageCursor !== undefined,
    refetchInterval: (query) =>
      query.state.data?.messages.some((m) => m.translationStatus === 'stale') ? 2_000 : false,
  });

  // Fold the HEAD page into the accumulated list. mergeHeadPage keeps loaded older history
  // (so the live tail flows no matter how far back the chatter has paged) BUT treats the head
  // page as authoritative for its own newest window — so a row the server dropped from that
  // window (a peer's delete / any deleted_at write) disappears, and a >50-message burst can't
  // strand an un-pageable interior gap (it collapses to the contiguous head page). "Delete
  // this message": drop any id deleted this session BEFORE merging, so a re-sync that races the
  // in-flight DELETE (the 5s poll / onSettled refetch, or a server that hasn't committed
  // deleted_at yet) can't resurrect a just-deleted bubble.
  React.useEffect(() => {
    const data = headQuery.data;
    if (!data) return;
    const incoming = data.messages.filter((m) => !deletedMessageIds.current.has(m.id));
    setAllMessages((prev) => mergeHeadPage(prev, incoming));
    // Conversation-scoped (not page-scoped) — the server returns them on every page, so any
    // head refetch keeps the pending bubbles current. Missing = older API (deploy skew).
    setPendingSends((data.pendingSends ?? []).filter((s) => !deletedSendIds.current.has(s.sendId)));
    // hasMore = "older history exists beyond the OLDEST loaded row". The head owns it until
    // the chatter starts paging; after that each OLDER page owns it (a head poll must never
    // resurrect the "Load older" button once they've reached the start of history) — EXCEPT
    // when this head page is disjoint from the loaded set: mergeHeadPage then collapses to the
    // head page (dropping older history to stay contiguous), so we re-establish the not-paged
    // invariant — reset the cursor + take head's hasMore — otherwise a paged-to-start collapse
    // would strand the dropped history behind a stale hasMore=false (Load-older hidden).
    const collapsed = headWindowDisjoint(allMessagesRef.current, incoming);
    if (collapsed && messageCursorRef.current !== undefined) setMessageCursor(undefined);
    if (collapsed || messageCursorRef.current === undefined) setHasMore(data.hasMore);
  }, [headQuery.data]);

  // Fold a cursor-driven OLDER page into the accumulated list (prepend via the same id-merge
  // → dedup + chronological). This page defines the new oldest boundary, so it owns hasMore.
  // Its arrival is the data commit the load-older re-anchor (layout effect branch A) waits on.
  React.useEffect(() => {
    const data = olderQuery.data;
    if (!data) return;
    const incoming = data.messages.filter((m) => !deletedMessageIds.current.has(m.id));
    // Signal the layout effect that THIS commit is the older page (its re-anchor target) —
    // set before setAllMessages so it's visible when branch A runs for the resulting render.
    olderCommittedRef.current = true;
    setAllMessages((prev) => mergeMessagesById(prev, incoming));
    setHasMore(data.hasMore);
  }, [olderQuery.data]);

  // "Load older messages" — page backwards from the oldest loaded row. Capture the
  // viewport height BEFORE the older page prepends so the layout effect can re-anchor
  // (otherwise the growing content shoves everything down / jumps to the bottom).
  //
  // Flag lifecycle is kept bulletproof so it can never dangle (a stuck flag would freeze
  // scroll-pin tracking and mis-anchor the next unrelated update):
  //   - Re-entry is gated on isPrependingRef (the precise "a prepend is in flight" signal)
  //     — NOT olderQuery.isFetching, which is also true during 2s background polls and
  //     would silently swallow legitimate clicks.
  //   - The flag is ARMED only when the cursor genuinely advances (a real state change that
  //     is guaranteed to produce a data commit → cleared by the layout effect) — never on a
  //     no-op setMessageCursor.
  //   - If the cursor can't advance (a prior load errored or returned only already-loaded
  //     rows, so oldest === the current cursor), retry the same key via refetch(). Both
  //     paths arm the flag so the successful prepend is re-anchored MANUALLY (branch A) —
  //     browsers without native scroll-anchoring (Safari/WebKit) won't hold it otherwise.
  const loadOlderMessages = React.useCallback(() => {
    if (isPrependingRef.current) return;
    const oldest = allMessages[0];
    if (!oldest) return;
    const el = threadScrollRef.current;
    if (el) {
      prevScrollHeightRef.current = el.scrollHeight;
      isPrependingRef.current = true;
    }
    if (oldest.sentAt === messageCursor) {
      // setMessageCursor would be a no-op (same value) — retry the current older key instead
      // (e.g. the previous page errored, so oldest hasn't advanced past the cursor).
      void olderQuery.refetch();
    } else {
      setMessageCursor(oldest.sentAt);
    }
  }, [allMessages, messageCursor, olderQuery]);

  // An older-page load that settles WITHOUT committing data (a failed fetch, including a
  // failed retry) never runs the layout effect that consumes the prepend flag. Clear it
  // here on a failed settle so it can't hijack the re-anchor branch of a later unrelated
  // update. Watches the OLDER query — the only fetch a prepend ever arms (load-older drives
  // it via setMessageCursor or olderQuery.refetch). Guarded on isError so this can't race the
  // SUCCESS re-anchor (on success, the layout effect's branch A clears the flag after commit).
  React.useEffect(() => {
    if (!olderQuery.isFetching && olderQuery.isError) isPrependingRef.current = false;
  }, [olderQuery.isFetching, olderQuery.isError]);

  // Track whether the chatter is pinned to the newest message. Scrolling up releases the
  // pin (so history reading is never yanked); scrolling back down re-arms it.
  const handleThreadScroll = React.useCallback(() => {
    const el = threadScrollRef.current;
    if (!el || isPrependingRef.current) return; // ignore the programmatic re-anchor scroll
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  }, []);

  // Callback ref on the MessageThread root: (re)attach a ResizeObserver whenever the
  // content element mounts/unmounts (loading spinner ↔ real thread). The observer re-pins
  // to the bottom on any content growth WHILE pinned — this is what keeps the open-snap at
  // the true bottom as lazy media/thumbnails decode in, and follows new messages.
  const attachThreadContent = React.useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const el = threadScrollRef.current;
      if (el && stickToBottomRef.current && !isPrependingRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    });
    ro.observe(node);
    resizeObserverRef.current = ro;
  }, []);

  // Pre-paint scroll positioning (owner UX 2026-07-13). Runs before the browser paints so
  // neither the open-snap nor the load-older re-anchor ever flickers:
  //   (A) "Load older" prepended above the viewport → keep the chatter anchored to what
  //       they were reading. Consume the prepend flag ONLY on the older query's own commit
  //       (olderCommittedRef) — the live-tail split means a head/tail commit can interleave
  //       mid-prepend, and it must not steal the flag and strand the real prepend. A commit
  //       with no real top-growth (empty older page / fully deduped) still clears the flag
  //       and falls through instead of yanking the viewport.
  //   (B) First paint of a conversation → snap to the newest message INSTANTLY (opening a
  //       chat should just be at the bottom, not animate up from the top). The observer
  //       above keeps it pinned there while lazy media finishes loading.
  React.useLayoutEffect(() => {
    const el = threadScrollRef.current;
    if (!el) return;

    if (olderCommittedRef.current) {
      olderCommittedRef.current = false; // consume the signal on the older commit
      if (isPrependingRef.current) {
        isPrependingRef.current = false;
        const grew = el.scrollHeight - prevScrollHeightRef.current;
        if (grew > 0) {
          el.scrollTop += grew;
          stickToBottomRef.current = false; // reading history, not pinned to the bottom
          return;
        }
        // Not a real prepend (empty older page / fully deduped) — fall through.
      }
    }

    if (allMessages.length + pendingSends.length === 0) return;

    if (!initialSnapDoneRef.current) {
      el.scrollTop = el.scrollHeight;
      initialSnapDoneRef.current = true;
      stickToBottomRef.current = true;
    }
  }, [allMessages, pendingSends]);

  // Live health for this conversation's account (D-18 / D-06)
  React.useEffect(() => {
    const unsub = ws.onHealthUpdate((evt: HealthUpdate) => {
      if (evt.accountId === conv.accountId) setHealthState(evt.state);
    });
    return unsub;
  }, [ws, conv.accountId]);

  // Mark read on open AND whenever unread re-appears while the thread is open.
  // Resyncs re-write unread from Maloum's unreadMessages (reconciler ~20s poll /
  // dev activation refresh) and CRM markRead never propagates to Maloum, so
  // without the conv.unread dep the re-asserted badge would stick until remount.
  // The isMarkedUnread override is consumed on first open only (NOT a dep, and
  // mark-unread never touches unread) — so marking the OPEN row unread does not
  // instantly self-revert.
  const openedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const firstOpen = openedRef.current !== conversationId;
    openedRef.current = conversationId;
    if (conv.unread > 0 || (firstOpen && conv.isMarkedUnread)) {
      onMarkRead?.(conv);
    }
  }, [conversationId, conv.unread]); // eslint-disable-line react-hooks/exhaustive-deps

  const name = fanDisplayName({
    nickname: conv.fanNickname,
    username: conv.fanUsername,
    fanId: conv.fanId,
  });

  return (
    <div className="flex flex-col h-full min-w-0 bg-bg">
      {/* Fan header */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-bg-card shrink-0">
        <FanAvatar
          nickname={conv.fanNickname}
          username={conv.fanUsername}
          fanId={conv.fanId}
          active
          size="md"
        />
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-semibold text-text truncate">{name}</span>
          {/* Subtitle: the Maloum username when a nickname is shown as the title —
              never a raw id (owner 2026-07-02: internal ids are noise to chatters). */}
          {conv.fanUsername && conv.fanUsername !== name && (
            <span className="text-xs text-text-subtle truncate">@{conv.fanUsername}</span>
          )}
          {/* Passive collision indicator below the fan name (UI-SPEC Surface 8 / SC-6).
              Renders nothing when unclaimed or when the viewer is the claimer. */}
          <CollisionChip
            claimedByChatterId={claim.claimedByChatterId}
            claimedAt={claim.claimedAt}
            currentChatterId={currentChatterId}
            claimerName={
              claim.claimedByChatterId
                ? chatterNameById.get(claim.claimedByChatterId)
                : undefined
            }
          />
        </div>

        {healthState && healthState !== 'online' && (
          <AccountHealthBadge state={healthState} compact />
        )}

        {/* Translation-mode indicator (Design Spec §04; reflects fan_profile.auto_translate) */}
        {conv.autoTranslate ? (
          <span
            className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border bg-bg text-xs text-text-muted shrink-0"
            title="Incoming German is translated to English; your English replies are sent as German."
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3.5 h-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h7M7.5 7v0M9 7c0 4-2.5 7-5 8M6 11c1.8 1.8 3.6 2.6 5 3M13 21l4-9 4 9M14.6 18h4.8" />
            </svg>
            Auto-translate
            <span className="font-mono font-semibold text-text">DE&nbsp;⇄&nbsp;EN</span>
          </span>
        ) : (
          <span
            className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-accent/40 bg-accent/10 text-xs text-accent shrink-0"
            title="This fan chats in English. Messages are shown as-is and your replies are sent verbatim — no translation."
          >
            English
            <span className="font-mono font-semibold">no translation</span>
          </span>
        )}

        {/* Mute toggle — CRM-local; moved here from the fan panel (owner 2026-07-01).
            Hidden entirely in readOnly (D-02) — mute is a chatter queue-management action. */}
        {!readOnly && (
          <button
            type="button"
            onClick={() => (conv.isMuted ? unmuteMut.mutate() : muteMut.mutate())}
            disabled={muteBusy || !fanMemberId}
            aria-pressed={fanMemberId ? conv.isMuted : undefined}
            title={
              !fanMemberId
                ? 'Mute unavailable — fan not reconciled yet'
                : conv.isMuted
                  ? 'Unmute — bring this chat back to the active queue'
                  : 'Mute — hide this chat from the active queue'
            }
            className={[
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs shrink-0 transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
              conv.isMuted
                ? 'border-accent/40 bg-accent/10 text-accent'
                : 'border-border bg-bg text-text-muted hover:text-text',
              muteBusy || !fanMemberId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ].join(' ')}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-3.5 h-3.5"
              aria-hidden="true"
            >
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
              {conv.isMuted && <line x1="3" y1="3" x2="21" y2="21" />}
            </svg>
            {conv.isMuted ? 'Muted' : 'Mute'}
          </button>
        )}
      </div>

      {/* Thread */}
      <div
        ref={threadScrollRef}
        onScroll={handleThreadScroll}
        className="flex-1 min-h-0 overflow-y-auto"
      >
        {!headQuery.isLoading && allMessages.length === 0 && pendingSends.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-6 text-center gap-2">
            <p className="text-sm text-text-muted">No messages yet.</p>
            <p className="text-xs text-text-subtle max-w-xs">
              {conv.autoTranslate
                ? 'Write the first message in English below — Maloum sends it to the fan in German.'
                : 'This fan chats in English. Write your message below — it is sent verbatim, no translation.'}
            </p>
          </div>
        ) : (
          <MessageThread
            contentRef={attachThreadContent}
            messages={allMessages}
            // Full-screen "Loading messages…" only on the first head fetch; the "Load older
            // messages" button spinner reflects an in-flight OLDER page (isLoading, not
            // isFetching, so 2s/5s background polls of the older page don't blink it).
            isLoading={headQuery.isLoading || olderQuery.isLoading}
            hasMore={hasMore}
            onLoadMore={loadOlderMessages}
            pendingSends={pendingSends}
            // Retry/delete are write actions — omitted entirely in readOnly so
            // MessageThread's affordances never render (D-02: no delete control).
            onRetrySend={
              readOnly
                ? undefined
                : (send) =>
                    setRetrySeed({
                      key: Date.now(),
                      en: send.originalEn,
                      isPpv: send.isPpv,
                      priceAmountMinor: send.priceAmountMinor,
                      hadMedia: send.hasMedia,
                    })
            }
            onDeleteMessage={readOnly ? undefined : (messageId) => deleteMessageMut.mutate(messageId)}
            onDeleteSend={readOnly ? undefined : (sendId) => deleteSendMut.mutate(sendId)}
          />
        )}
      </div>

      {/* Composer — hidden entirely in readOnly (D-02): the admin read-only surface never
          sends/PPVs. Chatter mode (readOnly falsy) is unchanged. */}
      {!readOnly && (
        <div className="shrink-0">
          <SendComposer
            conversationId={conversationId}
            accountHealthState={healthState}
            accountId={conv.accountId}
            fanMemberId={conv.fanMemberId ?? undefined}
            autoTranslate={conv.autoTranslate}
            retrySeed={retrySeed}
            onMessageSent={() => {
              // Re-pin to the bottom so the chatter always sees their just-sent message,
              // even if they had scrolled up to read history before replying (the pending
              // bubble grows content at the bottom → the ResizeObserver re-pins).
              stickToBottomRef.current = true;
              // Refetch the head (surfaces the new pending bubble + reconciled row). No cursor
              // reset — the head query already shows the tail, and keeping loaded older history
              // avoids falsely re-showing the "Load older" button (head-owned hasMore).
              queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
              onMessageSent?.();
            }}
          />
        </div>
      )}
    </div>
  );
}
